// src/pages/workloads/WorkloadAssignmentLogPage.tsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  ClipboardList,
  Search,
  Filter,
  Download,
  RefreshCw,
  Calendar,
  User,
  Layers,
  ChevronDown,
  Loader2,
  Plus,
  UserPlus,
  UserMinus,
  Activity,
  Link,
  MapPin,
  Archive,
  Trash2,
  RotateCcw,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { assignmentService } from '../../services/assignment.service';
import {
  WorkloadAssignmentLog,
  AssignmentAction,
  getActionLabel,
  getActionColor,
} from '../../types/assignment.types';
import toast from 'react-hot-toast';

const ACTION_ICONS: Record<AssignmentAction, React.ReactNode> = {
  created: <Plus className="h-4 w-4" />,
  assigned: <UserPlus className="h-4 w-4" />,
  unassigned: <UserMinus className="h-4 w-4" />,
  reassigned: <RefreshCw className="h-4 w-4" />,
  status_changed: <Activity className="h-4 w-4" />,
  survey_linked: <Link className="h-4 w-4" />,
  survey_unlinked: <Link className="h-4 w-4" />,
  addresses_added: <MapPin className="h-4 w-4" />,
  addresses_removed: <MapPin className="h-4 w-4" />,
  addresses_imported: <MapPin className="h-4 w-4" />,
  territory_updated: <Layers className="h-4 w-4" />,
  batch_update: <Layers className="h-4 w-4" />,
  archived: <Archive className="h-4 w-4" />,
  restored: <RotateCcw className="h-4 w-4" />,
  deleted: <Trash2 className="h-4 w-4" />,
};

const WorkloadAssignmentLogPage: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const [logs, setLogs] = useState<WorkloadAssignmentLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedActions, setSelectedActions] = useState<AssignmentAction[]>([]);
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: '',
    end: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  // Get the current project ID
  const currentProjectId = userProfile?.projectId || userProfile?.projects?.[0];

  // Load logs
  useEffect(() => {
    const loadLogs = async () => {
      if (!currentUser || !currentProjectId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const filters: any = {};
        if (selectedActions.length > 0) {
          filters.action = selectedActions;
        }
        if (dateRange.start && dateRange.end) {
          filters.dateRange = {
            start: new Date(dateRange.start),
            end: new Date(dateRange.end),
          };
        }

        const logData = await assignmentService.getProjectLogs(
          currentProjectId,
          filters,
          200
        );
        setLogs(logData);
      } catch (error) {
        console.error('Error loading logs:', error);
        toast.error('Failed to load assignment log');
      } finally {
        setIsLoading(false);
      }
    };

    loadLogs();
  }, [currentUser, currentProjectId, selectedActions, dateRange]);

  // Filter logs by search term
  const filteredLogs = logs.filter((log) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      log.workloadName.toLowerCase().includes(search) ||
      log.description.toLowerCase().includes(search) ||
      log.performedByName.toLowerCase().includes(search)
    );
  });

  // Export logs
  const handleExport = useCallback(async () => {
    if (!currentProjectId) return;

    try {
      const csvContent = await assignmentService.export(currentProjectId);
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `workload-assignment-log-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Log exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export log');
    }
  }, [currentProjectId]);

  // Toggle action filter
  const toggleActionFilter = (action: AssignmentAction) => {
    setSelectedActions((prev) =>
      prev.includes(action)
        ? prev.filter((a) => a !== action)
        : [...prev, action]
    );
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <ClipboardList className="h-7 w-7 mr-3 text-indigo-600" />
            Workload Assignment Log
          </h1>
          <p className="text-gray-600 mt-1">
            Track all assignment changes and workload modifications
          </p>
        </div>
        <button
          onClick={handleExport}
          className="flex items-center px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          <Download className="h-5 w-5 mr-2" />
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px] max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search logs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          {/* Filter toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center px-3 py-2 border rounded-md ${
              showFilters ? 'border-indigo-500 text-indigo-600' : 'border-gray-300 text-gray-700'
            }`}
          >
            <Filter className="h-5 w-5 mr-2" />
            Filters
            {selectedActions.length > 0 && (
              <span className="ml-2 bg-indigo-100 text-indigo-600 text-xs px-2 py-0.5 rounded-full">
                {selectedActions.length}
              </span>
            )}
          </button>

          {/* Refresh */}
          <button
            onClick={() => window.location.reload()}
            className="p-2 text-gray-400 hover:text-gray-600"
            title="Refresh"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
        </div>

        {/* Expanded filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="space-y-4">
              {/* Date range */}
              <div className="flex items-center gap-4">
                <Calendar className="h-5 w-5 text-gray-400" />
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange((prev) => ({ ...prev, start: e.target.value }))}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
                <span className="text-gray-500">to</span>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange((prev) => ({ ...prev, end: e.target.value }))}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>

              {/* Action type filters */}
              <div>
                <p className="text-sm text-gray-600 mb-2">Action Types</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    'created',
                    'assigned',
                    'unassigned',
                    'reassigned',
                    'status_changed',
                    'survey_linked',
                    'addresses_added',
                    'batch_update',
                    'archived',
                    'deleted',
                  ].map((action) => (
                    <button
                      key={action}
                      onClick={() => toggleActionFilter(action as AssignmentAction)}
                      className={`flex items-center px-3 py-1 text-xs rounded-full border ${
                        selectedActions.includes(action as AssignmentAction)
                          ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {ACTION_ICONS[action as AssignmentAction]}
                      <span className="ml-1">{getActionLabel(action as AssignmentAction)}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Clear filters */}
              <button
                onClick={() => {
                  setSelectedActions([]);
                  setDateRange({ start: '', end: '' });
                }}
                className="text-sm text-indigo-600 hover:text-indigo-700"
              >
                Clear all filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Log Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="p-12 text-center">
            <ClipboardList className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No log entries found</h3>
            <p className="text-gray-500 mt-2">
              Assignment activities will appear here.
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Workload
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  By
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredLogs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {log.performedAt.toDate().toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {log.workloadName}
                    </div>
                    {log.batchId && (
                      <div className="text-xs text-gray-500">
                        Batch: {log.batchSize} workloads
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: `${getActionColor(log.action)}20`,
                        color: getActionColor(log.action),
                      }}
                    >
                      {ACTION_ICONS[log.action]}
                      <span className="ml-1">{getActionLabel(log.action)}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-md truncate">
                      {log.description}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <User className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900">{log.performedByName}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Stats Summary */}
      {filteredLogs.length > 0 && (
        <div className="mt-4 text-sm text-gray-500 text-center">
          Showing {filteredLogs.length} log entries
        </div>
      )}
    </div>
  );
};

export default WorkloadAssignmentLogPage;
